/**
Project: Voice Panel (c)
Title: Account
Description: Componet to verify the account number for user to create the regions.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Flex, Text, Input, Button } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import { manageError } from "../../../../core/actions/common-actions";
import {
  setDomainSetupProgressAccountNumber,
  setDomainSetupProgressPage,
  getDomainStep,
} from "../actions/domain-setup-action";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import PageExitGuard from "../../common/helpers/page-exit-guard";

class Account extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_vpa_domainsetup = "vpa_domainsetup";
  constructor(props) {
    super(props);
    this.state = {
      isFormDataFilled: false,
      isFormDataValid: true,
      accountNumber: "",
      isError: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  checkFormData = async () => {
    if (
      this.props.subscriberAccount?.accountNumber !== "" &&
      this.props.subscriberAccount?.accountNumber >= 13
    ) {
      await this.setStateAsync({ isFormDataFilled: true });
    } else {
      await this.setStateAsync({ isFormDataFilled: false });
    }
  };

  handleFormFieldChange = async (e) => {
    let val = "";
    if (e.target.value) val = e.target.value.toUpperCase();
    await this.setStateAsync({
      [e.target.name]: val,
    });
    await this.checkFormData();
  };

  handleAccountSubmit = async () => {
    const regExp = /^[0-9A-Za-z]{8}[-][0-9]{4,7}$/;
    const isValid = regExp.test(this.props.subscriberAccount?.accountNumber);
    if (isValid) {
      this.setStateAsync({ isFormDataValid: true });
      const accountNumber =
        this.props.subscriberAccount?.accountNumber.toUpperCase();
      this.props.setDomainSetupProgressAccountNumber(accountNumber);
      await this.loadNextPage();
    } else {
      this.setStateAsync({ isFormDataValid: false });
    }
  };

  loadNextPage = async () => {
    try {
      this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_domainsetup,
          "notification.info.pleaseWaitUpdatingData"
        )
      );
      await this.props.getDomainStep(
        this._cancelToken
      );
      if (
        this.props.domainSetup.setupProcessStatus ===
        VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.NOT_STARTED
      ) {
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.REGIONS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.REGION
          );
          this.props.hidePageWaitDialog();
        }
      }
      if (
        this.props.domainSetup.setupProcessStatus ===
        VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.IN_PROGRESS
      ) {
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.REGIONS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.REGION
          );
          this.props.hidePageWaitDialog();
        }
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.RECORDS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.DOMAINS
          );
          this.props.hidePageWaitDialog();
        }
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.USERS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.USERS
          );
          this.props.hidePageWaitDialog();
        }
      }
      if (
        this.props.domainSetup.setupProcessStatus ===
        VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.COMPLETED
      ) {
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.REGIONS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.REGION
          );
          this.props.hidePageWaitDialog();
        }
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.RECORDS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.DOMAINS
          );
          this.props.hidePageWaitDialog();
        }
        if (
          this.props.domainSetup.progressItem ===
          VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.USERS
        ) {
          await this.props.setDomainSetupProgressPage(
            VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.USERS
          );
          this.props.hidePageWaitDialog();
        }
      }
    } catch (err) {
      this.props.hidePageWaitDialog();
      await this.setState({ isError: true });
      await manageError(err, this.props.history);
    }
  };

  render() {
    return (
      <Grid fluid className="page-grid m-t-15">
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "content.account.verifyAccountId"
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={12} md={12} lg={6} className="m-b-10">
            <Flex column gap="gap.medium" className="bg-gray p-15">
              <Text className="page-text">
                {IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.account.enterValidAccountId"
                )}
              </Text>
              <Input
                name="accountNumber"
                inverted
                value={this.props.subscriberAccount?.accountNumber}
                readOnly={
                  this.props.subscriberAccount?.accountNumber ? true : false
                }
                onChange={this.handleFormFieldChange}
                placeholder={IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.account.sampleAccountId"
                )}
                className="page-textbox border-b-primary-fade"
              />
            </Flex>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="m-t-15 m-b-15">
            <Button
              primary
              onClick={this.handleAccountSubmit}
              disabled={
                this.props.subscriberAccount?.accountNumber ||
                  this.state.isFormDataFilled
                  ? false
                  : true
              }
              content={IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "content.continue"
              )}
              className="vpa-voice-setup-button"
            />
            <PageExitGuard
              when={this.state.isError === false}
              navigate={(path) => this.props.history.push(path)}
              shouldBlockNavigation={(location) => {
                if (location.pathname) {
                  return true;
                }

                return false;
              }}
            />
          </Col>
        </Row>
        {this.state.isFormDataValid === false ? (
          <Row>
            <Col xs={12} className="m-b-15">
              <AlertErrorMessage
                content={
                  <>
                    {IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "notification.error.incorrectAccountId"
                    )}
                    <br />
                    {IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "notification.error.formatAccountIdSupport"
                    )}
                  </>
                }
              />
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={12} className="m-b-10">
            <Text className="page-text">
              <a
                href={`mailto:${VoiceAdminConstants.DOMAIN_SETUP.SIPPIO_SUPPORT_EMAIL
                  }?subject=${IntlUtil.getText(
                    this._intl_ns_vpa_domainsetup,
                    "content.account.emailSubject"
                  )}&body=${IntlUtil.getText(
                    this._intl_ns_vpa_domainsetup,
                    "content.account.emailBody"
                  )}`}
                className="page-link"
              >
                {IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.account.doNotKnowAccountId"
                )}
              </a>
            </Text>
          </Col>
        </Row>
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  subscriberAccount: state.subscriberStore.subscriberAccount,
  domainSetupProgressPage: state.domainSetupStore.domainSetupProgressPage,
  domainSetup: state.domainSetupStore.domainSetup,
});

const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
  setDomainSetupProgressAccountNumber,
  setDomainSetupProgressPage,
  getDomainStep,
};

export default connect(mapStateToProps, mapActionToProps)(Account);
